<template>
  <div class="ledger-page">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>

    <template v-else-if="asset">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ asset.asset_code }}
            </h3>
            <div>{{ displayName(asset) }}</div>
            <b-badge
              class="mt-50 mr-50"
              variant="light-primary"
            >
              {{ enumLabel(asset.category, $i18n.locale) }}
            </b-badge>
            <b-badge
              class="mt-50"
              :variant="statusBadgeVariant(enumValue(asset.status))"
            >
              {{ enumLabel(asset.status, $i18n.locale) }}
            </b-badge>
            <div
              v-if="categoryCodes"
              class="text-muted small mt-50"
            >
              {{ categoryCodes }}
            </div>
          </div>
          <b-button
            v-if="canDelete"
            variant="outline-danger"
            size="sm"
            class="rounded-pill"
            :disabled="deleting"
            @click="confirmDelete"
          >
            {{ $t('ledger_assets_delete') }}
          </b-button>
        </div>

        <div class="ledger-asset-summary mt-1">
          <div>
            <span class="text-muted">{{ $t('ledger_assets_cost') }}</span>
            <strong class="ledger-num d-block">{{ money(asset.cost) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_assets_accumulated') }}</span>
            <strong class="ledger-num d-block">{{ money(asset.accumulated_depreciation) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_assets_nbv') }}</span>
            <strong class="ledger-num d-block">{{ money(asset.net_book_value) }}</strong>
          </div>
          <div v-if="depreciable">
            <span class="text-muted">{{ $t('ledger_assets_monthly') }}</span>
            <strong class="ledger-num d-block">{{ money(asset.monthly_depreciation) }}</strong>
          </div>
        </div>

        <div class="mt-1">
          <b-link
            v-if="journalId && canOpenJournal"
            :to="{ name: 'ledger-journals-view', params: { id: journalId } }"
          >
            {{ $t('ledger_assets_acquisition_journal') }}
          </b-link>
        </div>
      </b-card>

      <b-card
        v-if="canEdit"
        class="rounded-lg"
      >
        <h5>{{ $t('ledger_assets_edit') }}</h5>
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group :label="$t('ledger_name')">
              <b-form-input v-model="form.name" />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group :label="$t('ledger_assets_name_ar')">
              <b-form-input v-model="form.name_ar" />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group :label="$t('ledger_assets_serial')">
              <b-form-input v-model="form.serial_number" />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group :label="$t('ledger_assets_location')">
              <b-form-input v-model="form.location" />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('ledger_assets_notes')">
              <b-form-textarea
                v-model="form.notes"
                rows="2"
              />
            </b-form-group>
          </b-col>
          <template v-if="depreciable">
            <b-col
              cols="6"
              md="4"
            >
              <b-form-group :label="$t('ledger_assets_salvage')">
                <b-form-input
                  v-model.number="form.salvage_value"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="termsLocked"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="6"
              md="4"
            >
              <b-form-group :label="$t('ledger_assets_life')">
                <b-form-input
                  v-model.number="form.useful_life_months"
                  type="number"
                  min="1"
                  step="1"
                  :disabled="termsLocked"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <b-form-group :label="$t('ledger_assets_start')">
                <b-form-input
                  v-model="form.depreciation_start_date"
                  type="date"
                  :disabled="termsLocked"
                />
              </b-form-group>
            </b-col>
          </template>
        </b-row>
        <p
          v-if="termsLocked"
          class="text-muted small"
        >
          {{ $t('ledger_assets_terms_locked') }}
        </p>
        <b-button
          variant="primary"
          class="rounded-pill"
          :disabled="saving"
          @click="save"
        >
          {{ $t('ledger_assets_save') }}
        </b-button>
      </b-card>

      <b-card
        v-if="depreciable"
        class="rounded-lg"
      >
        <h5>{{ $t('ledger_assets_history') }}</h5>
        <div
          v-if="!entries.length"
          class="text-muted"
        >
          {{ $t('ledger_assets_history_empty') }}
        </div>
        <div
          v-else
          class="d-none d-md-block"
        >
          <b-table
            :items="entries"
            :fields="historyFields"
            small
            responsive
            class="ledger-dense-table"
          >
            <template #cell(period)="data">
              <b-link
                v-if="data.item.run && data.item.run.id"
                :to="{ name: 'ledger-depreciation-view', params: { id: data.item.run.id } }"
              >
                {{ periodLabel(data.item.run) }}
              </b-link>
              <span v-else>{{ periodLabel(data.item.run) }}</span>
            </template>
            <template #cell(amount)="data">
              <span class="ledger-num">{{ money(data.item.amount) }}</span>
            </template>
            <template #cell(accumulated_after)="data">
              <span class="ledger-num">{{ money(data.item.accumulated_after) }}</span>
            </template>
            <template #cell(book_value_after)="data">
              <span class="ledger-num">{{ money(data.item.book_value_after) }}</span>
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="entry in entries"
            :key="entry.id"
            class="ledger-asset-card"
          >
            <strong>{{ periodLabel(entry.run) }}</strong>
            <div class="ledger-num">
              {{ money(entry.amount) }}
            </div>
            <div class="ledger-num small text-muted">
              {{ $t('ledger_assets_nbv') }} {{ money(entry.book_value_after) }}
            </div>
          </article>
        </div>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BTable, BLink, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getFixedAsset, updateFixedAsset, deleteFixedAsset } from '@/api/accounting/fixedAssets'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'FixedAssetShow',
  components: {
    BCard,
    BBadge,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BTable,
    BLink,
    BSpinner,
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      asset: null,
      form: {},
    }
  },
  computed: {
    depreciable() {
      const category = this.asset && this.asset.category
      if (category && typeof category === 'object' && category.depreciable != null) return Boolean(category.depreciable)
      return enumValue(category) !== 'land'
    },
    termsLocked() {
      return (this.entries || []).some(entry => enumValue(entry.run && entry.run.status) === 'posted')
    },
    canEdit() {
      return this.hasPermission('edit fixed_assets')
    },
    canDelete() {
      return this.hasPermission('delete fixed_assets') && !this.termsLocked
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    journalId() {
      if (!this.asset) return null
      return this.asset.acquisition_journal_entry_id
        || (this.asset.acquisition_journal_entry && this.asset.acquisition_journal_entry.id)
        || null
    },
    entries() {
      return (this.asset && this.asset.depreciation_entries) || []
    },
    categoryCodes() {
      const category = this.asset && this.asset.category
      if (!category || typeof category !== 'object') return ''
      const parts = [category.cost_code, category.accumulated_code, category.expense_code].filter(Boolean)
      return parts.join(' · ')
    },
    historyFields() {
      return [
        { key: 'period', label: this.$t('ledger_payroll_period') },
        { key: 'amount', label: this.$t('ledger_assets_amount') },
        { key: 'accumulated_after', label: this.$t('ledger_assets_accumulated') },
        { key: 'book_value_after', label: this.$t('ledger_assets_nbv') },
      ]
    },
  },
  watch: {
    '$route.params.id': 'load',
  },
  mounted() {
    this.load()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    displayName(asset) {
      if (this.$i18n.locale === 'en') return asset.name || asset.name_ar || ''
      return asset.name_ar || asset.name || ''
    },
    periodLabel(run) {
      if (!run) return '—'
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const month = new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, run.month - 1, 1))
      return `${month} ${run.year}`
    },
    syncForm(asset) {
      this.form = {
        name: asset.name || '',
        name_ar: asset.name_ar || '',
        serial_number: asset.serial_number || '',
        location: asset.location || '',
        notes: asset.notes || '',
        salvage_value: Number(asset.salvage_value) || 0,
        useful_life_months: asset.useful_life_months,
        depreciation_start_date: asset.depreciation_start_date || '',
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async load() {
      this.loading = true
      try {
        const res = await getFixedAsset(this.$route.params.id)
        this.asset = oneFrom(res)
        this.syncForm(this.asset || {})
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canEdit || this.saving) return
      const body = {
        name: String(this.form.name || '').trim(),
        name_ar: String(this.form.name_ar || '').trim() || null,
        serial_number: String(this.form.serial_number || '').trim() || null,
        location: String(this.form.location || '').trim() || null,
        notes: String(this.form.notes || '').trim() || null,
      }
      if (this.depreciable && !this.termsLocked) {
        body.salvage_value = roundMoney(this.form.salvage_value || 0)
        body.useful_life_months = Number(this.form.useful_life_months)
        if (this.form.depreciation_start_date) body.depreciation_start_date = this.form.depreciation_start_date
      }
      this.saving = true
      try {
        await updateFixedAsset(this.asset.id, body)
        this.toast(this.$t('ledger_assets_saved'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
        if (error?.response?.status === 422) await this.load()
      } finally {
        this.saving = false
      }
    },
    async confirmDelete() {
      if (!this.canDelete || this.deleting) return
      const result = await Swal.fire({
        text: this.$t('ledger_assets_delete_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_assets_delete'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.deleting = true
      try {
        await deleteFixedAsset(this.asset.id)
        this.toast(this.$t('ledger_assets_deleted'), 'success')
        this.$router.push({ name: 'ledger-assets' })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-asset-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
@media (min-width: 768px) {
  .ledger-asset-summary {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.ledger-asset-card {
  border-bottom: 1px solid #eee;
  padding: 0.75rem 0.15rem;
}
</style>
